import { useMemo, useState } from "react";
import { ChevronDown, X, Palette, Tag, BadgeCheck, PoundSterling, Check } from "lucide-react";
import { condLabel, gbp } from "@/lib/api";
import { colourSwatch } from "@/lib/colourSwatches";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Sticky, one-thumb filter bar for the search results. Filters the already-
 * fetched result set on the client (inventory is small, so this is instant
 * with no extra network round-trips). Facet options are derived from the
 * current results, so we never show a filter that returns nothing.
 */
export default function SmartFilterBar({ listings = [], value, onChange }) {
  const [open, setOpen] = useState(null); // "colour" | "brand" | "condition" | "budget"

  const facets = useMemo(() => {
    const colours = new Map();
    const brands = new Map();
    const conditions = new Map();
    let maxPrice = 0;
    for (const l of listings) {
      if (l.colour) {
        const c = l.colour.toLowerCase();
        colours.set(c, (colours.get(c) || 0) + 1);
      }
      if (l.manufacturer) brands.set(l.manufacturer, (brands.get(l.manufacturer) || 0) + 1);
      if (l.condition) conditions.set(l.condition, (conditions.get(l.condition) || 0) + 1);
      if (typeof l.price_gbp === "number") maxPrice = Math.max(maxPrice, l.price_gbp);
    }
    return {
      colours: [...colours.entries()].sort((a, b) => b[1] - a[1]),
      brands: [...brands.entries()].sort((a, b) => b[1] - a[1]),
      conditions: [...conditions.entries()],
      maxPrice: Math.ceil(maxPrice / 10) * 10 || 0,
    };
  }, [listings]);

  const budgetPresets = useMemo(() => {
    const mx = facets.maxPrice;
    if (!mx) return [];
    return [50, 100, 150, 200, 300, 500, 750].filter((p) => p < mx);
  }, [facets.maxPrice]);

  const set = (patch) => onChange({ ...value, ...patch });
  const clearAll = () => {
    onChange({ colour: "", brand: "", condition: "", maxPrice: 0 });
    setOpen(null);
  };
  const activeCount = ["colour", "brand", "condition", "maxPrice"].filter((k) => value[k]).length;
  const toggle = (id) => setOpen((o) => (o === id ? null : id));

  const chips = [
    { id: "colour", icon: Palette, label: value.colour ? cap(value.colour) : "Colour", show: facets.colours.length > 1, active: !!value.colour },
    { id: "brand", icon: Tag, label: value.brand || "Brand", show: facets.brands.length > 1, active: !!value.brand },
    { id: "condition", icon: BadgeCheck, label: value.condition ? condLabel(value.condition) : "Condition", show: facets.conditions.length > 1, active: !!value.condition },
    { id: "budget", icon: PoundSterling, label: value.maxPrice ? `Under ${gbp(value.maxPrice)}` : "Budget", show: budgetPresets.length > 0, active: !!value.maxPrice },
  ].filter((c) => c.show);

  if (chips.length === 0) return null;

  return (
    <div
      className="sticky top-16 md:top-[96px] z-30 bg-white/95 backdrop-blur border-b border-slate-200 -mx-4 sm:-mx-6 px-4 sm:px-6 mb-4"
      data-testid="smart-filter-bar"
    >
      <div className="flex items-center gap-2 overflow-x-auto py-3 no-scrollbar">
        {chips.map(({ id, icon: Icon, label, active }) => (
          <button
            key={id}
            type="button"
            onClick={() => toggle(id === "budget" ? "budget" : id)}
            className={`inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full border text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
              active
                ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]"
                : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
            }`}
            data-testid={`filter-chip-${id}`}
            aria-expanded={open === id}
          >
            <Icon className="w-4 h-4" aria-hidden="true" />
            {label}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open === id ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        ))}
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 h-10 px-3 rounded-full text-sm font-semibold text-slate-600 hover:text-[#E05915] whitespace-nowrap shrink-0"
            data-testid="filter-clear-all"
          >
            <X className="w-4 h-4" aria-hidden="true" /> Clear ({activeCount})
          </button>
        )}
      </div>

      {open && (
        <div className="pb-4" data-testid={`filter-panel-${open}`}>
          {open === "colour" && (
            <div className="flex flex-wrap gap-2">
              {facets.colours.map(([c, n]) => {
                const selected = value.colour === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { set({ colour: selected ? "" : c }); setOpen(null); }}
                    className={`inline-flex items-center gap-2 h-10 pl-2 pr-3.5 rounded-full border text-sm font-medium capitalize transition-colors ${
                      selected ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                    data-testid={`filter-colour-${c.replace(/\s+/g, "-")}`}
                  >
                    <span className="w-6 h-6 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: colourSwatch(c) }} aria-hidden="true" />
                    {c} <span className="text-xs text-slate-400">({n})</span>
                    {selected && <Check className="w-4 h-4" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          )}

          {open === "brand" && (
            <div className="flex flex-wrap gap-2">
              {facets.brands.map(([b, n]) => {
                const selected = value.brand === b;
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => { set({ brand: selected ? "" : b }); setOpen(null); }}
                    className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-full border text-sm font-medium transition-colors ${
                      selected ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                    data-testid={`filter-brand-${b.replace(/\s+/g, "-")}`}
                  >
                    {b} <span className="text-xs text-slate-400">({n})</span>
                    {selected && <Check className="w-4 h-4" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          )}

          {open === "condition" && (
            <div className="flex flex-wrap gap-2">
              {facets.conditions.map(([c, n]) => {
                const selected = value.condition === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { set({ condition: selected ? "" : c }); setOpen(null); }}
                    className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-full border text-sm font-medium transition-colors ${
                      selected ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                    data-testid={`filter-condition-${c}`}
                  >
                    {condLabel(c)} <span className="text-xs text-slate-400">({n})</span>
                    {selected && <Check className="w-4 h-4" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          )}

          {open === "budget" && (
            <div className="flex flex-wrap gap-2">
              {budgetPresets.map((p) => {
                const selected = value.maxPrice === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { set({ maxPrice: selected ? 0 : p }); setOpen(null); }}
                    className={`inline-flex items-center gap-1 h-10 px-3.5 rounded-full border text-sm font-medium transition-colors ${
                      selected ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                    data-testid={`filter-budget-${p}`}
                  >
                    Under {gbp(p)}
                    {selected && <Check className="w-4 h-4" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
